import React, { useState } from 'react';
import './App.css';
import { Book, Trip, Hobby, INITIAL_BOOKS, INITIAL_TRIPS, INITIAL_HOBBIES, FUN_MESSAGES, TRIP_MESSAGES, BOOK_EMOJIS, TRIP_EMOJIS } from './booksData';

function App() {
  const [books, setBooks] = useState<Book[]>(INITIAL_BOOKS);
  const [trips, setTrips] = useState<Trip[]>(INITIAL_TRIPS);
  const [hobbies] = useState<Hobby[]>(INITIAL_HOBBIES);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const [title, setTitle] = useState('');
  const [rating, setRating] = useState(5);
  const [emoji, setEmoji] = useState('📚');
  const [favoriteCharacter, setFavoriteCharacter] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  // Trip form states
  const [destination, setDestination] = useState('');
  const [tripDate, setTripDate] = useState('');
  const [tripEmoji, setTripEmoji] = useState('🏖️');
  const [highlights, setHighlights] = useState('');
  const [favoriteMemory, setFavoriteMemory] = useState('');
  const [showTripForm, setShowTripForm] = useState(false);

  const addBook = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim() === '') return;

    const newBook: Book = {
      id: Date.now(),
      title: title,
      rating: rating,
      emoji: emoji,
      dateRead: new Date().toLocaleDateString(),
      favoriteCharacter: favoriteCharacter || undefined,
    };

    setBooks([newBook, ...books]);
    setTitle('');
    setRating(5);
    setEmoji('📚');
    setFavoriteCharacter('');
    setShowForm(false);
    
    // Show fun message
    const message = FUN_MESSAGES[Math.floor(Math.random() * FUN_MESSAGES.length)];
    alert(message);
  };

  const deleteBook = (id: number) => {
    setBooks(books.filter(book => book.id !== id));
  };

  const addTrip = (e: React.FormEvent) => {
    e.preventDefault();
    if (destination.trim() === '') return;

    const newTrip: Trip = {
      id: Date.now(),
      destination: destination,
      date: tripDate || new Date().toLocaleDateString(),
      emoji: tripEmoji,
      highlights: highlights,
      favoriteMemory: favoriteMemory || undefined,
    };

    setTrips([newTrip, ...trips]);
    setDestination('');
    setTripDate('');
    setTripEmoji('🏖️');
    setHighlights('');
    setFavoriteMemory('');
    setShowTripForm(false);
    
    const message = TRIP_MESSAGES[Math.floor(Math.random() * TRIP_MESSAGES.length)];
    alert(message);
  };

  const deleteTrip = (id: number) => {
    setTrips(trips.filter(trip => trip.id !== id));
  };

  const totalBooks = books.length;
  const averageRating = books.length > 0 
    ? (books.reduce((sum, book) => sum + book.rating, 0) / books.length).toFixed(1)
    : 0;

  return (
    <div className="App">
      <header className="header">
        <h1 className="title">
          <span className="sparkle">✨</span>
          Ashra's Magical Scrapbook
          <span className="sparkle">✨</span>
        </h1>
        <p className="subtitle">My Special Collection of Stories, Adventures & Dreams! 🌟</p>
      </header>

      <div className="intro-section">
        <div className="picture-frame">
          <img src="/ashra.jpg" alt="Ashra" className="ashra-photo" />
        </div>
        <div className="intro-text">
          <h2>� Welcome to My World! 💕</h2>
          <p>
            Hi! I'm Ashra, and I'm 7 years old! This is my special scrapbook where I keep 
            all my favorite things - the books I've read, the amazing places I've visited, 
            and all the fun activities I love to do! ✨
          </p>
          <p className="intro-quote">
            "Every day is a new adventure waiting to happen!" 🦋💭🌈
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button 
          className="tab-button"
          onClick={() => scrollToSection('books-section')}
        >
          <span className="tab-emoji">📚</span>
          <span className="tab-label">My Books</span>
          <span className="tab-count">{books.length}</span>
        </button>
        <button 
          className="tab-button"
          onClick={() => scrollToSection('trips-section')}
        >
          <span className="tab-emoji">✈️</span>
          <span className="tab-label">My Travels</span>
          <span className="tab-count">{trips.length}</span>
        </button>
        <button 
          className="tab-button"
          onClick={() => scrollToSection('hobbies-section')}
        >
          <span className="tab-emoji">🎨</span>
          <span className="tab-label">My Hobbies</span>
          <span className="tab-count">{hobbies.length}</span>
        </button>
      </nav>



      <div className="main-content">
        <button className="add-book-btn" onClick={() => setShowForm(!showForm)}>
          <span className="btn-emoji">📖</span>
          {showForm ? 'Never mind' : '+ Add a Book'}
        </button>
        {showForm && (
          <form className="book-form" onSubmit={addBook}>
            <h2 className="form-title">🎉 Tell us about your book! 🎉</h2>
            
            <div className="form-group">
              <label>📖 Book Title:</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's the book called?"
                required
              />
            </div>

            <div className="form-group">
              <label>⭐ How many stars? (1-5):</label>
              <div className="rating-selector">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star-btn ${rating >= star ? 'active' : ''}`}
                    onClick={() => setRating(star)}
                  >
                    {rating >= star ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>🎨 Pick a fun emoji for this book:</label>
              <div className="emoji-selector">
                {BOOK_EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    className={`emoji-btn ${emoji === e ? 'active' : ''}`}
                    onClick={() => setEmoji(e)}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>🎭 Favorite Character (optional):</label>
              <input
                type="text"
                value={favoriteCharacter}
                onChange={(e) => setFavoriteCharacter(e.target.value)}
                placeholder="Who was your favorite?"
              />
            </div>

            <div className="form-buttons">
              <button type="submit" className="submit-btn">
                Save Book! 🎊
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => {
                  setShowForm(false);
                  setTitle('');
                  setFavoriteCharacter('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Books Section */}
        <div id="books-section" className="section-content">
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-emoji">📚</div>
              <div className="stat-number">{totalBooks}</div>
              <div className="stat-label">Books Read</div>
            </div>
            <div className="stat-card">
              <div className="stat-emoji">⭐</div>
              <div className="stat-number">{averageRating}</div>
              <div className="stat-label">Avg Rating</div>
            </div>
            <div className="stat-card">
              <div className="stat-emoji">🏆</div>
              <div className="stat-number">{totalBooks > 10 ? 'Master' : totalBooks > 5 ? 'Expert' : 'Rising'}</div>
              <div className="stat-label">Reader Level</div>
            </div>
          </div>
          <h2 className="section-title">📚 My Reading Collection</h2>
          <div className="books-container">
            {books.length === 0 ? (
              <div className="empty-state">
                <div className="empty-emoji">📚</div>
                <h3>No books yet!</h3>
                <p>Start adding the wonderful books you've read! 🌈</p>
              </div>
            ) : (
              <div className="books-grid">
                {books.map((book) => (
                  <div key={book.id} className="book-card">
                    <button className="delete-btn" onClick={() => deleteBook(book.id)}>✕</button>
                    <div className="book-emoji">{book.emoji}</div>
                    <h3 className="book-title">{book.title}</h3>
                    <div className="book-rating">
                      {'⭐'.repeat(book.rating)}
                    </div>
                    {book.favoriteCharacter && (
                      <p className="book-character">
                        💝 Loved: {book.favoriteCharacter}
                      </p>
                    )}
                    <p className="book-date">📅 {book.dateRead}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Trips Section */}
        <div id="trips-section" className="section-content">
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-emoji">🗺️</div>
              <div className="stat-number">{trips.length}</div>
              <div className="stat-label">Places Visited</div>
            </div>
            <div className="stat-card">
              <div className="stat-emoji">🌟</div>
              <div className="stat-number">{trips.length > 5 ? 'Explorer' : 'Adventurer'}</div>
              <div className="stat-label">Travel Level</div>
            </div>
          </div>
          <h2 className="section-title">✈️ My Travel Adventures</h2>
          <button className="add-book-btn" onClick={() => setShowTripForm(!showTripForm)}>
            <span className="btn-emoji">✈️</span>
            {showTripForm ? 'Never mind' : '+ Add a Trip'}
          </button>
          {showTripForm && (
            <form className="book-form" onSubmit={addTrip}>
              <h2 className="form-title">🌍 Tell us about your trip!</h2>
              <div className="form-group">
                <label>Destination:</label>
                <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Where did you go?" required />
              </div>
              <div className="form-group">
                <label>Date:</label>
                <input type="date" value={tripDate} onChange={(e) => setTripDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Pick an emoji:</label>
                <div className="emoji-selector">
                  {TRIP_EMOJIS.map((e) => (
                    <button key={e} type="button" className={`emoji-btn ${tripEmoji === e ? 'active' : ''}`} onClick={() => setTripEmoji(e)}>{e}</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Highlights:</label>
                <input type="text" value={highlights} onChange={(e) => setHighlights(e.target.value)} placeholder="What did you do?" required />
              </div>
              <div className="form-group">
                <label>Favourite Memory (optional):</label>
                <input type="text" value={favoriteMemory} onChange={(e) => setFavoriteMemory(e.target.value)} placeholder="Best moment?" />
              </div>
              <div className="form-buttons">
                <button type="submit" className="submit-btn">Save Trip! 🎉</button>
                <button type="button" className="cancel-btn" onClick={() => setShowTripForm(false)}>Cancel</button>
              </div>
            </form>
          )}
          <div className="trips-container">
            {trips.length === 0 ? (
              <div className="empty-state">
                <div className="empty-emoji">✈️</div>
                <h3>No trips yet!</h3>
                <p>Start adding the amazing places you've visited! 🌍</p>
              </div>
            ) : (
              <div className="trips-grid">
                {trips.map((trip) => (
                  <div key={trip.id} className="trip-card">
                    <button className="delete-btn" onClick={() => deleteTrip(trip.id)}>✕</button>
                    <div className="trip-emoji">{trip.emoji}</div>
                    <h3 className="trip-destination">{trip.destination}</h3>
                    <p className="trip-date">📅 {trip.date}</p>
                    <p className="trip-highlights">
                      <strong>✨ What I Did:</strong> {trip.highlights}
                    </p>
                    {trip.favoriteMemory && (
                      <p className="trip-memory">
                        💝 Best Memory: {trip.favoriteMemory}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Hobbies Section */}
        <div id="hobbies-section" className="section-content">
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-emoji">💫</div>
              <div className="stat-number">{hobbies.length}</div>
              <div className="stat-label">Active Hobbies</div>
            </div>
            <div className="stat-card">
              <div className="stat-emoji">⭐</div>
              <div className="stat-number">{hobbies.filter(h => h.level === 'Expert').length}</div>
              <div className="stat-label">Expert Skills</div>
            </div>
          </div>
          <h2 className="section-title">🎨 Things I Love to Do</h2>
          <div className="hobbies-container">
            {hobbies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-emoji">🎨</div>
                <h3>No hobbies yet!</h3>
                <p>Start adding the fun activities you love! 🎪</p>
              </div>
            ) : (
              <div className="hobbies-grid">
                {hobbies.map((hobby) => (
                  <div key={hobby.id} className="hobby-card">
                    <div className="hobby-emoji">{hobby.emoji}</div>
                    <h3 className="hobby-name">{hobby.name}</h3>
                    <p className="hobby-description">{hobby.description}</p>
                    <div className="hobby-level">
                      <span className={`level-badge level-badge--${hobby.level.toLowerCase()}`}>{hobby.level}</span>
                    </div>
                    <p className="hobby-date">Started: {hobby.startedDate}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="footer">
        <p>Keep exploring, learning, and having fun, Ashra! 🌟�🦋</p>
      </footer>
    </div>
  );
}

export default App;
